import { Observable } from 'rxjs';
import { UserService } from './user.service';

/** Poll interval while waiting for the post-login role-flag chain to finish writing. */
const ROLE_FLAGS_POLL_INTERVAL_MS = 100;

/** Hard cap on the wait -- past this, resolve with whatever role flags are currently set rather than block navigation indefinitely. */
const ROLE_FLAGS_WAIT_TIMEOUT_MS = 3000;

/**
 * Resolves the dashboard route an authenticated user lands on by default.
 *
 * Single source of the role-priority rules (department head > SPNI > Trent > standard).
 * Shared so the post-login landing (`AuthRedirectComponent.navigateToDefaultDashboard()`)
 * and the app's entry redirect (`entryRedirect`) cannot drift apart — re-entering the app
 * must land the user exactly where a fresh login would have.
 *
 * Every path returned here is a real route in `routes-routing.module.ts` — a missing one
 * would fall through to the `**` wildcard and loop back into `entryRedirect`.
 *
 * @param userService Session identity source supplying the role flags.
 * @returns Absolute path of the user's default dashboard.
 */
export function defaultDashboardPath(userService: UserService): string {
  if (userService.getIsHead()) {
    return '/departmentHeadDashboard';
  }
  if (userService.getSpniUser() === 'true') {
    return '/spniDashboard';
  }
  if (userService.getTrentUser() === 'true') {
    return '/trentDashboard';
  }
  return '/dashboard';
}

/**
 * Resolves `defaultDashboardPath()` once the post-login role-flag chain
 * (`UserService.areRoleFlagsLoaded()`) has finished writing to sessionStorage,
 * instead of reading it while it may still be in flight (the chain is kicked
 * off by `LoginComponent.onSubmit()` in parallel with navigation to OTP/security
 * questions, so it is not guaranteed to have finished by the time the user
 * completes that step). Polls sessionStorage directly rather than relying on an
 * in-memory signal from `LoginComponent`, so it resolves correctly even if the
 * OTP/security-questions screen was reached via a page reload rather than a
 * fresh login.
 *
 * Falls back to whatever `defaultDashboardPath()` returns once
 * `ROLE_FLAGS_WAIT_TIMEOUT_MS` elapses, so the user always lands somewhere --
 * worst case, this reproduces today's race exactly.
 *
 * @param userService Session identity source supplying the role flags.
 * @returns Observable emitting the absolute path of the user's default dashboard exactly once.
 */
export function defaultDashboardPath$(userService: UserService): Observable<string> {
  return new Observable<string>((subscriber) => {
    const startedAt = Date.now();

    /**
     * Checks whether the role-flag chain has finished (or the wait has timed out) and,
     * if so, emits the resolved dashboard path; otherwise reschedules itself.
     * @returns {void} No return value.
     */
    const check = () => {
      const ready = userService.areRoleFlagsLoaded();
      const timedOut = Date.now() - startedAt >= ROLE_FLAGS_WAIT_TIMEOUT_MS;
      if (ready || timedOut) {
        if (timedOut && !ready) {
          console.warn('defaultDashboardPath$: role flags not loaded within timeout, falling back to current values');
        }
        subscriber.next(defaultDashboardPath(userService));
        subscriber.complete();
        return;
      }
      timerId = setTimeout(check, ROLE_FLAGS_POLL_INTERVAL_MS);
    };

    let timerId = setTimeout(check, 0);
    return () => clearTimeout(timerId);
  });
}
